/*
 * Match-detail landmark emblem per team.
 *
 * On the match detail hero, a large desaturated emoji (or inline SVG) is
 * painted behind each team's flag as a "head-to-head" decoration — e.g.
 * 🗼 Eiffel for France, 🍁 maple leaf for Canada. Independent of the user's
 * favorite team: halos render for every match, both teams.
 *
 * Design handoff had 3 options per team (Landmark Picker tool) so the user
 * could choose. We ship the first option as the default — no user picker
 * in the real app yet.
 *
 * 3 custom SVG strings (Scotland bagpipe, Bosnia Mostar bridge, Egypt pyramids)
 * are preserved verbatim from the design handoff. Web renders them via
 * `dangerouslySetInnerHTML`; native currently falls back to a plain emoji
 * since react-native-svg doesn't accept raw SVG strings — the three exceptions
 * are called out in `NATIVE_FALLBACK` below.
 */

export interface TeamLandmark {
  /** Stable id — used if we ever add a picker. */
  id: string;
  /** Short human-readable name — "Eiffel Tower", "Maple leaf". */
  name: string;
  /**
   * Emoji character OR an inline SVG string (starts with `<svg …`).
   * Callers must detect `str.trim().startsWith('<svg')` and render SVG as
   * HTML on platforms that support it.
   */
  emoji: string;
}

// ---------- Custom SVG illustrations ----------
// Used where no emoji exists for the landmark. The viewBox + style is baked
// in so they render at roughly the same glyph size as emoji. Web renderers
// inject them via `dangerouslySetInnerHTML`; native falls back to the
// NATIVE_FALLBACK emoji (see bottom of file) since Metro / Hermes can't
// parse raw SVG strings.

const BAGPIPE_SVG = `<svg viewBox="5 8 90 90" xmlns="http://www.w3.org/2000/svg" style="width:1.1em;height:1.1em;vertical-align:middle;display:inline-block">
  <ellipse cx="40" cy="58" rx="24" ry="20" fill="#2a5f3e"/>
  <path d="M20,52 Q40,44 60,52 M18,62 Q40,56 62,62 M22,70 Q40,66 58,70" stroke="#8b1a1a" stroke-width="1.5" fill="none" opacity=".8"/>
  <path d="M30,42 L30,74 M40,40 L40,78 M50,42 L50,74" stroke="#8b1a1a" stroke-width="1.5" opacity=".8"/>
  <rect x="36" y="18" width="4" height="24" rx="1" fill="#3b2410"/>
  <rect x="34" y="16" width="8" height="4" rx="1" fill="#d4a24a"/>
  <rect x="58" y="14" width="3" height="36" rx="1" fill="#3b2410" transform="rotate(15 59.5 32)"/>
  <rect x="66" y="18" width="3" height="32" rx="1" fill="#3b2410" transform="rotate(20 67.5 34)"/>
  <rect x="74" y="22" width="3" height="28" rx="1" fill="#3b2410" transform="rotate(25 75.5 36)"/>
  <rect x="58" y="12" width="4" height="3" fill="#d4a24a" transform="rotate(15 60 13.5)"/>
  <rect x="66" y="16" width="4" height="3" fill="#d4a24a" transform="rotate(20 68 17.5)"/>
  <rect x="74" y="20" width="4" height="3" fill="#d4a24a" transform="rotate(25 76 21.5)"/>
  <rect x="34" y="72" width="4" height="22" rx="1" fill="#3b2410"/>
  <circle cx="36" cy="78" r="1" fill="#1a0d05"/>
  <circle cx="36" cy="84" r="1" fill="#1a0d05"/>
  <circle cx="36" cy="90" r="1" fill="#1a0d05"/>
</svg>`;

const PYRAMID_SVG = `<svg viewBox="0 20 100 75" xmlns="http://www.w3.org/2000/svg" style="width:1.15em;height:1.15em;vertical-align:middle;display:inline-block">
  <defs>
    <linearGradient id="pyrSky" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#f4c36a" stop-opacity=".0"/>
      <stop offset="1" stop-color="#e8a23a" stop-opacity=".25"/>
    </linearGradient>
    <linearGradient id="pyrLit" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stop-color="#d9a86a"/>
      <stop offset="1" stop-color="#b8864a"/>
    </linearGradient>
    <linearGradient id="pyrShade" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stop-color="#8e6736"/>
      <stop offset="1" stop-color="#6b4d27"/>
    </linearGradient>
  </defs>
  <rect x="0" y="30" width="100" height="55" fill="url(#pyrSky)"/>
  <circle cx="78" cy="40" r="7" fill="#f4c36a" opacity=".55"/>
  <polygon points="16,82 32,50 48,82" fill="url(#pyrShade)"/>
  <polygon points="32,50 48,82 40,82" fill="#6b4d27" opacity=".55"/>
  <line x1="32" y1="50" x2="32" y2="82" stroke="#4a3518" stroke-width=".5" opacity=".5"/>
  <polygon points="30,85 58,32 86,85" fill="url(#pyrLit)"/>
  <polygon points="58,32 86,85 70,85" fill="url(#pyrShade)"/>
  <polygon points="56,38 58,32 60,38" fill="#e8c58a"/>
  <line x1="58" y1="32" x2="58" y2="85" stroke="#4a3518" stroke-width=".6" opacity=".55"/>
  <g stroke="#a07a42" stroke-width=".4" opacity=".45" fill="none">
    <path d="M42,70 L58,70"/><path d="M46,62 L58,62"/><path d="M50,54 L58,54"/><path d="M54,46 L58,46"/>
  </g>
  <polygon points="60,85 80,42 100,85" fill="url(#pyrLit)"/>
  <polygon points="80,42 100,85 86,85" fill="url(#pyrShade)"/>
  <polygon points="78,47 80,42 82,47" fill="#e8c58a"/>
  <line x1="80" y1="42" x2="80" y2="85" stroke="#4a3518" stroke-width=".5" opacity=".5"/>
  <path d="M0,82 Q25,78 50,82 T100,82 L100,95 L0,95 Z" fill="#c89d5e"/>
  <path d="M0,88 Q30,85 55,88 T100,88" fill="none" stroke="#a67a3e" stroke-width=".6" opacity=".6"/>
</svg>`;

const MOSTAR_SVG = `<svg viewBox="0 30 100 65" xmlns="http://www.w3.org/2000/svg" style="width:1.15em;height:1.15em;vertical-align:middle;display:inline-block">
  <rect x="0" y="60" width="100" height="40" fill="#3d8bb8" opacity=".5"/>
  <path d="M0,78 Q20,74 40,78 T80,78 T100,78 L100,100 L0,100 Z" fill="#2a6d93" opacity=".55"/>
  <path d="M8,68 L8,60 Q8,58 10,58 L22,58 Q24,58 24,60 C24,46 76,46 76,60 Q76,58 78,58 L90,58 Q92,58 92,60 L92,68 Z" fill="#c9a77a" stroke="#6b4e2a" stroke-width="1"/>
  <path d="M24,60 C24,48 76,48 76,60" fill="none" stroke="#6b4e2a" stroke-width="1.2"/>
  <g stroke="#8a6840" stroke-width=".6" fill="none" opacity=".7">
    <path d="M30,54 L30,58"/><path d="M38,51 L38,57"/><path d="M46,49 L46,56"/>
    <path d="M54,49 L54,56"/><path d="M62,51 L62,57"/><path d="M70,54 L70,58"/>
  </g>
  <rect x="12" y="56" width="76" height="2" fill="#8a6840"/>
  <rect x="4" y="40" width="10" height="28" fill="#b89466" stroke="#6b4e2a" stroke-width=".8"/>
  <rect x="86" y="40" width="10" height="28" fill="#b89466" stroke="#6b4e2a" stroke-width=".8"/>
  <polygon points="4,40 9,32 14,40" fill="#7a5a32"/>
  <polygon points="86,40 91,32 96,40" fill="#7a5a32"/>
  <path d="M7,48 a2,3 0 0 1 4,0 L11,53 L7,53 Z" fill="#3b2410"/>
  <path d="M89,48 a2,3 0 0 1 4,0 L93,53 L89,53 Z" fill="#3b2410"/>
  <path d="M24,82 C24,70 76,70 76,82" fill="none" stroke="#c9a77a" stroke-width="1" opacity=".35"/>
</svg>`;

const L = (id: string, name: string, emoji: string): TeamLandmark => ({ id, name, emoji });

/**
 * Default landmark per team — selected from the handoff's 3-option picker.
 * Keyed by fifaCode. Missing codes fall back to a plain football emoji.
 */
export const TEAM_LANDMARKS: Record<string, TeamLandmark> = {
  // Group A
  CZE: L('castleCze', 'Prague castle', '🏰'),
  KOR: L('kimchi', 'Kimchi', '🌶️'),
  MEX: L('taco', 'Taco', '🌮'),
  RSA: L('elephant', 'Elephant', '🐘'),
  // Group B
  BIH: L('mostar', 'Mostar bridge', MOSTAR_SVG),
  CAN: L('maple', 'Maple leaf', '🍁'),
  QAT: L('mosque', 'Mosque', '🕌'),
  SUI: L('alps', 'Swiss Alps', '🏔️'),
  // Group C
  BRA: L('samba', 'Carnival', '🎉'),
  HAI: L('hibiscus', 'Hibiscus', '🌺'),
  MAR: L('lanternMar', 'Marrakech lantern', '🪔'),
  SCO: L('bagpipe', 'Bagpipes', BAGPIPE_SVG),
  // Group D
  AUS: L('kangaroo', 'Kangaroo', '🦘'),
  PAR: L('parrot', 'Macaw', '🦜'),
  TUR: L('balloon', 'Cappadocia balloons', '🎈'),
  USA: L('liberty', 'Statue of Liberty', '🗽'),
  // Group E
  CIV: L('cocoaCiv', 'Cocoa', '🍫'),
  CUW: L('flamingo', 'Flamingo', '🦩'),
  ECU: L('banana', 'Banana', '🍌'),
  GER: L('beer', 'Stein of beer', '🍺'),
  // Group F
  JPN: L('torii', 'Torii gate', '⛩️'),
  NED: L('tulip', 'Tulips', '🌷'),
  SWE: L('meatball', 'Meatballs', '🍝'),
  TUN: L('sidi', 'Sidi Bou Said', '🌊'),
  // Group G
  BEL: L('waffle', 'Waffle', '🧇'),
  EGY: L('pyramid', 'Pyramids of Giza', PYRAMID_SVG),
  IRN: L('persepolis', 'Persepolis gate', '🏺'),
  NZL: L('kiwiBird', 'Kiwi bird', '🥝'),
  // Group H
  CPV: L('turtle', 'Sea turtle', '🐢'),
  ESP: L('paella', 'Paella', '🥘'),
  KSA: L('camelKsa', 'Arabian camel', '🐪'),
  URU: L('asado', 'Asado grill', '🥩'),
  // Group I
  FRA: L('eiffel', 'Eiffel Tower', '🗼'),
  IRQ: L('ziggurat', 'Ziggurat of Ur', '🏛️'),
  NOR: L('reindeer', 'Reindeer', '🦌'),
  SEN: L('drum', 'Djembe drum', '🥁'),
  // Group J
  ALG: L('fennec', 'Fennec fox', '🦊'),
  ARG: L('tango', 'Tango dancers', '💃'),
  AUT: L('alps2', 'Alpine peaks', '⛷️'),
  JOR: L('petra', 'Petra', '🏛️'),
  // Group K
  COD: L('rumba', 'Rumba drum', '🥁'),
  COL: L('coffee', 'Colombian coffee', '☕'),
  POR: L('guitar', 'Fado guitar', '🎸'),
  UZB: L('samarkand', 'Registan Square', '🕌'),
  // Group L
  CRO: L('coast', 'Dalmatian coast', '🏖️'),
  ENG: L('bigBen', 'Big Ben', '🕰️'),
  GHA: L('kente', 'Kente cloth', '🧶'),
  PAN: L('hatPan', 'Panama hat', '👒'),
};

/**
 * Native fallback: the three teams whose default landmark is a custom SVG
 * get an emoji alternative instead, since raw `<svg>` strings can't be
 * rendered on React Native without a full svg-string → react-native-svg
 * parser (not worth the complexity for 3 teams).
 */
export const NATIVE_FALLBACK_LANDMARKS: Record<string, TeamLandmark> = {
  SCO: L('bagpipe-native', 'Bagpipes', '🎶'),
  EGY: L('pyramid-native', 'Pyramids of Giza', '🏜️'),
  BIH: L('mostar-native', 'Mostar bridge', '🌉'),
};

/** Falls back to `⚽` when the code is unknown. */
export function getTeamLandmark(fifaCode: string | null | undefined): TeamLandmark {
  if (!fifaCode) return L('ball', 'Football', '⚽');
  return TEAM_LANDMARKS[fifaCode.toUpperCase()] ?? L('ball', 'Football', '⚽');
}

/**
 * Native-safe variant. On the three SVG-illustrated teams, returns an emoji
 * alternative so native renderers don't have to parse raw SVG strings.
 */
export function getTeamLandmarkNative(fifaCode: string | null | undefined): TeamLandmark {
  if (!fifaCode) return L('ball', 'Football', '⚽');
  const code = fifaCode.toUpperCase();
  return NATIVE_FALLBACK_LANDMARKS[code] ?? TEAM_LANDMARKS[code] ?? L('ball', 'Football', '⚽');
}

/** True iff the emoji field is a raw SVG string (web-only rendering). */
export function isSvgLandmark(emoji: string): boolean {
  return typeof emoji === 'string' && emoji.trim().startsWith('<svg');
}

/** Country-possessive helper: "France" → "France's", "Wales" → "Wales'". */
export function landmarkPossessive(name: string): string {
  if (!name) return '';
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}
